import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { JessModalProps } from "../../types/tour";
import jessImage from "../../assets/images/jess.png";
import styles from "./JessModal.module.scss";

const JessModal: React.FC<JessModalProps> = ({
  isOpen,
  currentStep,
  stepIndex,
  totalSteps,
  steps,
  isWaitingForInteraction,
  canGoNext,
  canGoBack,
  onNext,
  onBack,
  onSkip,
  onClose,
  targetSelector,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const [modalPosition, setModalPosition] = useState<{
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  }>({});
  const [arrowDirection, setArrowDirection] = useState<
    "left" | "right" | "top" | "bottom"
  >("left");

  // Calcular posição dinâmica baseada no elemento alvo
  useEffect(() => {
    if (!isOpen || !targetSelector) {
      setModalPosition({});
      return;
    }

    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) {
      setModalPosition({});
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Dimensões estimadas do modal
    const modalWidth = 380;
    const modalHeight = 280;

    const position: typeof modalPosition = {};

    // Tentar posicionar à direita do elemento
    const rightSpace = windowWidth - (rect.right + scrollLeft);
    if (rightSpace >= modalWidth + 20) {
      position.left = rect.right + scrollLeft + 20;
      // Posicionar mais acima - alinhado com o topo do elemento + offset para cima
      position.top = Math.max(20, rect.top + scrollTop - 80);
      setArrowDirection("left"); // Seta aponta para a esquerda (para o elemento)
    }
    // Se não couber à direita, tentar à esquerda
    else if (rect.left + scrollLeft >= modalWidth + 20) {
      position.right = windowWidth - (rect.left + scrollLeft) + 20;
      // Posicionar mais acima - alinhado com o topo do elemento + offset para cima
      position.top = Math.max(20, rect.top + scrollTop - 80);
      setArrowDirection("right"); // Seta aponta para a direita (para o elemento)
    }
    // Se não couber em nenhum lado, posicionar embaixo
    else {
      position.left = Math.max(
        20,
        rect.left + scrollLeft + rect.width / 2 - modalWidth / 2,
      );
      position.top = rect.bottom + scrollTop + 20;
      setArrowDirection("top"); // Seta aponta para cima (para o elemento)
    }

    // Garantir que não saia da tela
    if (position.top !== undefined) {
      position.top = Math.min(position.top, windowHeight - modalHeight - 20);
      position.top = Math.max(position.top, 20);
    }

    setModalPosition(position);
  }, [isOpen, targetSelector, stepIndex]);

  // Foco automático no botão "Próximo" quando disponível
  useEffect(() => {
    if (isOpen && canGoNext && nextButtonRef.current) {
      nextButtonRef.current.focus();
    }
  }, [isOpen, canGoNext]);

  // Suporte a teclado
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          event.preventDefault();
          // Perguntar se deseja pular o tour
          if (window.confirm(t("interactiveTour.confirmSkip"))) {
            onSkip?.();
          }
          break;
        case "ArrowRight":
        case "Enter":
          event.preventDefault();
          if (canGoNext) {
            onNext?.();
          }
          break;
        case "ArrowLeft":
          event.preventDefault();
          if (canGoBack) {
            onBack?.();
          }
          break;
        case "Tab":
          // Manter foco dentro do modal
          if (modalRef.current) {
            const focusableElements = modalRef.current.querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );
            const firstElement = focusableElements[0] as HTMLElement;
            const lastElement = focusableElements[
              focusableElements.length - 1
            ] as HTMLElement;

            if (event.shiftKey) {
              if (document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
              }
            } else {
              if (document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
              }
            }
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, canGoNext, canGoBack, onNext, onBack, onSkip, t]);

  if (!isOpen) {
    return null;
  }

  const currentStepObject = steps?.[currentStep];
  const progressPercentage =
    totalSteps > 0
      ? (((stepIndex !== undefined ? stepIndex : currentStep) + 1) /
          totalSteps) *
        100
      : 0;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="jess-modal-title"
      style={{
        position: "fixed",
        top: modalPosition.top,
        left: modalPosition.left,
        right: modalPosition.right,
        bottom: modalPosition.bottom,
        width:
          modalPosition.top !== undefined || modalPosition.bottom !== undefined
            ? "380px"
            : "auto",
        height: "auto",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: 0,
      }}
    >
      <div
        ref={modalRef}
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Seta apontando para o elemento */}
        <div className={`${styles.arrow} ${styles[`arrow-${arrowDirection}`]}`}>
          <div className={styles.arrowInner}></div>
        </div>

        {/* Header com Avatar da Jess */}
        <div className={styles.header}>
          <div className={styles.avatarContainer}>
            <img src={jessImage} alt="Jess Avatar" className={styles.avatar} />
            <div className={styles.avatarGlow}></div>
          </div>

          <div className={styles.headerContent}>
            <h3 id="jess-modal-title" className={styles.title}>
              {currentStepObject?.title || t("interactiveTour.defaultTitle")}
            </h3>

            {/* Barra de Progresso */}
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <span className={styles.progressText}>
                {(stepIndex !== undefined ? stepIndex : currentStep) + 1}{" "}
                {t("interactiveTour.of")} {totalSteps}
              </span>
            </div>
          </div>

          {/* Botão Fechar */}
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("interactiveTour.close")}
            title={t("interactiveTour.close")}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className={styles.content}>
          <div className={styles.description}>
            {typeof currentStepObject?.content === "string"
              ? currentStepObject.content
              : t("interactiveTour.defaultContent")}
          </div>

          {/* Indicador de Interação Necessária */}
          {isWaitingForInteraction && (
            <div className={styles.interactionIndicator}>
              <div className={styles.pulseIcon}>
                <i className="fas fa-hand-pointer" />
              </div>
              <span className={styles.interactionText}>
                {t("interactiveTour.waitingForInteraction")}
              </span>
            </div>
          )}
        </div>

        {/* Footer com Botões */}
        <div className={styles.footer}>
          <div className={styles.buttonGroup}>
            {/* Botão Voltar */}
            <button
              className={`${styles.button} ${styles.backButton}`}
              onClick={onBack}
              disabled={!canGoBack}
              aria-label={t("interactiveTour.back")}
            >
              <i className="fas fa-chevron-left" />
              {t("interactiveTour.back")}
            </button>

            {/* Botão Pular */}
            <button
              className={`${styles.button} ${styles.skipButton}`}
              onClick={onSkip}
              aria-label={t("interactiveTour.skip")}
            >
              {t("interactiveTour.skip")}
            </button>

            {/* Botão Próximo/Concluir */}
            <button
              ref={nextButtonRef}
              className={`${styles.button} ${styles.nextButton} ${!canGoNext ? styles.disabled : ""}`}
              onClick={onNext}
              disabled={!canGoNext}
              aria-label={
                stepIndex === totalSteps - 1
                  ? t("interactiveTour.finish")
                  : t("interactiveTour.next")
              }
            >
              {stepIndex === totalSteps - 1 ? (
                <>
                  {t("interactiveTour.finish")}
                  <i className="fas fa-check" />
                </>
              ) : (
                <>
                  {t("interactiveTour.next")}
                  <i className="fas fa-chevron-right" />
                </>
              )}
            </button>
          </div>

          {/* Dica de Teclado */}
          <div className={styles.keyboardHint}>
            <span className={styles.hintText}>
              {t("interactiveTour.keyboardHint")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JessModal;
